import React from 'react';
import PropTypes from 'prop-types';

import { useField, useFormikContext } from 'formik';
import Label from '~components/forms/Label';
import FieldHelpText from '~components/forms/HelpText';
import { IMaskInput } from 'react-imask';

export const inputStylesNoError =
  'input input-bordered w-full bg-base-200 border-base-content text-base focus:border-base-content focus:border-2 focus:outline-none';
export const inputStylesError =
  'input input-bordered input-primary w-full bg-base-200 text-base focus:border-2 focus:outline-none';

function InputGroup({
  name,
  extraLabelledBy,
  label,
  type,
  placeholder,
  pattern,
  title,
  inputMode,
  autoComplete,
  helpText,
  elementId,
  required,
  children,
  showAsOptional,
  errors,
  isSubfield,
  inputMask,
  onBlur,
  disabled,
}) {
  const [field, meta] = useField({ name });
  const error = !!(meta.touched && meta.error) || !!errors?.[name];
  const ariaLabels = [`label__${name}`];
  const formikProps = useFormikContext();

  if (extraLabelledBy) {
    ariaLabels.push(extraLabelledBy);
  }

  if (helpText) {
    ariaLabels.push(`helptext__${name}`);
  }

  const ariaLabelledBy = ariaLabels.join(' ');

  const modifiedLabel = showAsOptional ? `${label} (optional)` : label;
  return (
    <div className='form-control w-full'>
      <Label
        htmlFor={name}
        id={`label__${name}`}
        isRequired={required}
        isSubfield={isSubfield}
      >
        {modifiedLabel}
      </Label>
      {children ||
        (inputMask ? (
          <IMaskInput
            mask={inputMask}
            className={error ? inputStylesError : inputStylesNoError}
            onAccept={(value) => {
              formikProps.setFieldValue(name, value);
            }}
            onBlur={onBlur}
            type={type}
            name={name}
            pattern={pattern}
            inputMode={inputMode}
            id={name}
            aria-labelledby={ariaLabelledBy}
            autoComplete={autoComplete}
            data-element-id={elementId}
            required={required}
            disabled={disabled}
            title={title}
            placeholder={placeholder}
            {...field}
          />
        ) : (
          <input
            className={error ? inputStylesError : inputStylesNoError}
            type={type}
            name={name}
            pattern={pattern}
            inputMode={inputMode}
            id={name}
            aria-labelledby={ariaLabelledBy}
            autoComplete={autoComplete}
            data-element-id={elementId}
            required={required}
            disabled={disabled}
            title={title}
            placeholder={placeholder}
            {...field}
            onBlur={onBlur || field.onBlur} // Regular inputs accept onBlur
          />
        ))}

      {error && (
        <FieldHelpText isError>{meta.error || errors?.[name]}</FieldHelpText>
      )}
      {helpText && (
        <FieldHelpText id={`helptext__${name}`}>{helpText}</FieldHelpText>
      )}
    </div>
  );
}

InputGroup.propTypes = {
  as: PropTypes.string,
  autoComplete: PropTypes.string,
  elementId: PropTypes.string,
  extraLabelledBy: PropTypes.string,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  pattern: PropTypes.string,
  title: PropTypes.string,
  inputMode: PropTypes.string,
  type: PropTypes.string,
  helpText: PropTypes.string,
  errors: PropTypes.object.isRequired,
  required: PropTypes.bool,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  showAsOptional: PropTypes.bool,
  isSubfield: PropTypes.bool,
  placeholder: PropTypes.string,
  inputMask: PropTypes.string,
  onBlur: PropTypes.func,
  disabled: PropTypes.bool,
};

InputGroup.defaultProps = {
  type: 'text',
  as: 'input',
  extraLabelledBy: '',
  showAsOptional: false,
  isSubfield: false,
  disabled: false,
};

export default InputGroup;
